/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useContext, useEffect, useRef, useState } from 'react'
import { getAssetPath } from '../../utils/assetPath'
import Icon from '../../components/shared/Icon'
import Dropdown from 'react-bootstrap/Dropdown'
import { useNavigate } from 'react-router'
import { useServiceContext } from '../../root'
import Events from '../../lib/Events'
import { SaveFilterType } from '../../constants/reportTypes'
import { MenuItem } from '../../../shims/react-bootstrap-MenuItem'

// Search type constants matching lego/page/search/Constants.js
const SEARCH_TYPE_V2 = {
  ALL: 'all',
  DOC_UID: 'docUID',
  CONTENT: 'content',
  FILE_NAME: 'filename',
  FILE_PATH: 'filepath',
  METADATA_VALUE: 'metadatavalue'
}

interface FilterOption {
  value: string
  label: string
}

interface SearchBarProps {
  defaultType?: string
  defaultQuery?: string
}

const SearchBar: React.FC<SearchBarProps> = ({ defaultType = 'filename', defaultQuery = '' }) => {
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const {
    service: {
      filter: { translate },
      init
    },
    api,
    bus
  } = useServiceContext()

  const filterOptions: FilterOption[] = [
    { value: 'filename', label: 'SEARCH_BY_NAME' },
    init.getFeaturesInfo()?.allFeaturesData?.elasticSearchEnabled && { value: 'content', label: 'SEARCH_BY_CONTENT' },
    { value: 'docUID', label: 'SEARCH_BY_DOCUMENT_IDENTIFIER' },
    { value: 'all', label: 'SEARCH_BY_ALL' }
  ].filter(Boolean) as FilterOption[]

  const getFilterByOption = (val: string): FilterOption => {
    return filterOptions.find((i) => i.value === val) || filterOptions[0]
  }

  const [filterBy, setFilterBy] = useState<FilterOption>(getFilterByOption(defaultType))
  const [searchQuery, setSearchQuery] = useState(defaultQuery)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [savedSearches, setSavedSearches] = useState<any[]>([])
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false)
  const subscriptions = useRef<(() => void)[]>([])
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)
  const unmounted = useRef(false)

  useEffect(() => {
    setSearchQuery(defaultQuery)
  }, [defaultQuery])

  useEffect(() => {
    if (bus) {
      const unsubscribe = bus.addEventListener(Events.UPDATE_SEARCH_BAR_FILTER, (event: any) => {
        const { payload } = event.detail || event
        if (payload?.phrase) setSearchQuery(payload.phrase)
        if (payload?.searchType) setFilterBy(getFilterByOption(payload.searchType))
      })
      subscriptions.current.push(unsubscribe)
    }

    return () => {
      subscriptions.current.forEach((sbs) => {
        if (typeof sbs === 'function') sbs()
      })
    }
  }, [bus])

  // Debounced effect for search suggestions
  useEffect(() => {
    if (searchQuery === defaultQuery) {
      return
    }
    if (!searchQuery) {
      hideSuggestions()
      return
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      if (init.getFeaturesInfo()?.allFeaturesData?.elasticSearchEnabled) {
        api.files
          .searchSuggestion(searchQuery)
          .then((data: any) => {
            if (unmounted.current) {
              return
            }
            const searchSuggestions: string[] = []

            if (data.fileNameSuggestion) {
              searchSuggestions.push(...data.fileNameSuggestion)
            }
            if (data.contentSuggestion) {
              searchSuggestions.push(...data.contentSuggestion)
            }

            setSuggestions(searchSuggestions.slice(0, 10))
            setShowSearchSuggestions(true)
          })
          .catch(() => {
            // Silently handle error
          })
      }
      return () => {
        unmounted.current = true
      }
    }, 800)

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [searchQuery, defaultQuery, init, api])

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        hideSuggestions()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Load saved searches
  useEffect(() => {
    let unmounted = false

    if (api.formFilters) {
      api.formFilters
        .getAllSavedReportsFilters({
          pageSize: 5,
          pageStart: 0,
          sortBy: 'createdAt',
          sortDesc: true,
          filterTypes: SaveFilterType.SEARCH
        })
        .then((data: any) => {
          if (!unmounted) {
            setSavedSearches(data.elements || [])
          }
        })
        .catch(() => {
          // Silently handle error
        })
    }

    return () => {
      unmounted = true
    }
  }, [api])

  const hideSuggestions = () => {
    setSuggestions([])
    setShowSearchSuggestions(false)
  }

  const search = (term: string) => {
    if (!term) {
      return
    }
    hideSuggestions()

    // TODO: Store search filter when formFilters API is available
    // For now, just navigate to search with query params

    // Navigate to search page using React Router
    const url = `/search?query=${encodeURIComponent(term)}&type=${filterBy.value}&show=${SEARCH_TYPE_V2.ALL}`
    try {
      navigate(url)
    } catch (error) {
      // Fallback to hash-based navigation if navigate fails
      window.location.hash = `#${url}`
    }
  }

  return (
    <div ref={ref} className="search-wrapper">
      <input
        type="text"
        autoComplete="off"
        className="search-input"
        placeholder=""
        value={searchQuery}
        onChange={(e) => {
          if (bus) {
            bus.dispatchEvent(
              new CustomEvent(Events.UPDATE_SEARCH_BAR_FILTER().type, {
                detail: { payload: { searchType: filterBy.value, phrase: e.target.value } }
              })
            )
          }
          setSearchQuery(e.target.value)
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            search(searchQuery)
          }
        }}
        maxLength={255}
      />
      <Dropdown id="dropdown-custom-menu" className="search-filter">
        <Dropdown.Toggle bsPrefix="noClass" variant="" id="search-dropdown-toggle">
          {translate(filterBy.label)} <span className="caret"></span>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {filterOptions.map((option) => {
            return option?.value !== filterBy.value ? (
              <MenuItem
                key={option?.value}
                className="filter-by-txt"
                eventKey={option?.value}
                onSelect={(eventKey: any) => {
                  // TODO: Store filter type when formFilters API is available
                  setFilterBy(option)
                  if (bus) {
                    bus.dispatchEvent(
                      new CustomEvent(Events.UPDATE_SEARCH_BAR_FILTER().type, {
                        detail: { payload: { searchType: eventKey?.value, phrase: searchQuery } }
                      })
                    )
                  }
                }}
              >
                {translate(option?.label)}
              </MenuItem>
            ) : null
          })}
          {savedSearches.length > 0 && (
            <>
              <MenuItem divider />
              <MenuItem disabled>{translate('MY_SAVED_SEARCH_SETTINGS_TITLE')}</MenuItem>
              {savedSearches.map((savedSearch) => {
                return (
                  <MenuItem
                    key={savedSearch.id}
                    eventKey={savedSearch}
                    onSelect={(eventKey: any) => {
                      navigate(`/search?filter=${eventKey.id}`)
                    }}
                  >
                    {savedSearch.name}
                  </MenuItem>
                )
              })}
              <MenuItem
                onSelect={() => {
                  navigate('/MySavedReportSettings?from=savedSearchPage')
                }}
              >
                {translate('MORE...')}
              </MenuItem>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
      <button
        onClick={() => {
          search(searchQuery)
        }}
        className="search-filter-btn"
        type="button"
        aria-label="Search"
      >
        <Icon iconPath="assets/images/icons/forms/icon_search.svg" viewBox="0 0 19 19" className="w100p h100p" />
      </button>
      {showSearchSuggestions && suggestions.length > 0 && (
        <div className="suggestions">
          <ul>
            {suggestions.map((item, index) => {
              return (
                <li key={index}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      search(item)
                    }}
                  >
                    {item}
                  </a>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

export { SearchBar }
export default SearchBar
